import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import type {
  ComplianceItem,
  ComplianceItemStatus,
  ComplianceItemPriority,
  ComplianceItemCategory,
  ComplianceSourceType,
  CompliancePhase,
  PhaseConfidence,
  DeliverableType,
} from '@/lib/schemas'
import { api } from '@/lib/api'

// ===================
// Types
// ===================

export type PromoteMode = 'append' | 'replace'

/** Pre-built view modes for the compliance matrix */
export type ComplianceView = 'submission_checklist' | 'response_compliance' | 'contract_obligations' | 'contract' | 'delivery' | 'all'

interface ComplianceItemFilters {
  status?: ComplianceItemStatus
  priority?: ComplianceItemPriority
  category?: ComplianceItemCategory
  sourceType?: ComplianceSourceType
  overdue?: boolean
  ownerId?: string
  // Phase classification filters
  phase?: CompliancePhase | CompliancePhase[]
  phaseConfidenceMin?: PhaseConfidence
  deliverableType?: DeliverableType
  sourceDocumentId?: string
  needsReview?: boolean
}

interface ComplianceItemsState {
  // State
  items: ComplianceItem[]
  isLoading: boolean
  error: string | null
  currentBidId: string | null

  // Filter state
  showSuperseded: boolean
  filters: ComplianceItemFilters
  activeView: ComplianceView
  includeUncertain: boolean

  // Promote job tracking
  isPromoting: boolean
  promoteJobId: string | null

  // Actions - Fetching
  fetchItems: (bidId: string) => Promise<void>

  // Actions - CRUD
  updateItem: (id: string, data: Partial<ComplianceItem>) => Promise<ComplianceItem>

  // Actions - Promote
  promoteItems: (bidId: string, mode: PromoteMode) => Promise<void>

  // Actions - Utility
  clearError: () => void
  reset: () => void
  setShowSuperseded: (show: boolean) => void
  setFilters: (filters: Partial<ComplianceItemFilters>) => void
  clearFilters: () => void
  setActiveView: (view: ComplianceView) => void
  setIncludeUncertain: (include: boolean) => void

  // Computed
  getActiveItems: () => ComplianceItem[]
  getFilteredItems: () => ComplianceItem[]
  getViewFilteredItems: () => ComplianceItem[]
  getItemsByStatus: (status: ComplianceItemStatus) => ComplianceItem[]
  getItemsByPriority: (priority: ComplianceItemPriority) => ComplianceItem[]
  getItemsByCategory: (category: ComplianceItemCategory) => ComplianceItem[]
  getItemsBySourceType: (sourceType: ComplianceSourceType) => ComplianceItem[]
  getOverdueItems: () => ComplianceItem[]
  getItemById: (id: string) => ComplianceItem | undefined
  getStats: () => {
    total: number
    pending: number
    inProgress: number
    completed: number
    blocked: number
    criticalPending: number
    highPending: number
    overdueCount: number
  }
  getViewStats: () => {
    total: number
    pending: number
    inProgress: number
    completed: number
    blocked: number
    criticalPending: number
    highPending: number
    overdueCount: number
  }
}

export const useComplianceItemsStore = create<ComplianceItemsState>()(
  devtools(
    (set, get) => ({
      // Initial state
      items: [],
      isLoading: false,
      error: null,
      currentBidId: null,
      showSuperseded: false,
      filters: {},
      activeView: 'response_compliance' as ComplianceView,
      includeUncertain: false,
      isPromoting: false,
      promoteJobId: null,

      // Fetch items for a bid
      fetchItems: async (bidId: string) => {
        try {
          set({ isLoading: true, error: null })
          const items = await api.get<ComplianceItem[]>(`/bids/${bidId}/compliance-items`)
          set({ items, isLoading: false, currentBidId: bidId })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch compliance items',
            isLoading: false,
            currentBidId: bidId,
          })
        }
      },

      // Update an item
      updateItem: async (id: string, data: Partial<ComplianceItem>) => {
        try {
          set({ error: null })
          const item = await api.patch<ComplianceItem>(`/compliance-items/${id}`, data)
          set((state) => ({
            items: state.items.map((i) => (i.id === id ? item : i)),
          }))
          return item
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to update compliance item',
          })
          throw error
        }
      },

      // Promote items (re-run extraction)
      promoteItems: async (bidId: string, mode: PromoteMode) => {
        try {
          set({ isPromoting: true, error: null })
          const result = await api.post<{ jobId: string }>(`/bids/${bidId}/compliance-items/promote`, { mode })
          set({ promoteJobId: result.jobId })
          // Refetch items after promotion
          await get().fetchItems(bidId)
          set({ isPromoting: false })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to promote compliance items',
            isPromoting: false,
          })
          throw error
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Reset store state
      reset: () => {
        set({
          items: [],
          isLoading: false,
          error: null,
          currentBidId: null,
          showSuperseded: false,
          filters: {},
          activeView: 'response_compliance' as ComplianceView,
          includeUncertain: false,
          isPromoting: false,
          promoteJobId: null,
        })
      },

      // Toggle showing superseded items
      setShowSuperseded: (show: boolean) => {
        set({ showSuperseded: show })
      },

      // Set filters
      setFilters: (filters: Partial<ComplianceItemFilters>) => {
        set((state) => ({ filters: { ...state.filters, ...filters } }))
      },

      // Clear all filters
      clearFilters: () => {
        set({ filters: {} })
      },

      // Set active compliance view
      setActiveView: (view: ComplianceView) => {
        set({ activeView: view })
      },

      // Toggle including uncertain/needs-review items
      setIncludeUncertain: (include: boolean) => {
        set({ includeUncertain: include })
      },

      // Get active (non-superseded) items
      // Note: compliance_items don't have artifactStatus in DB, so treat undefined as 'active'
      getActiveItems: () => {
        const { items, showSuperseded } = get()
        if (showSuperseded) return items
        return items.filter((i) => !i.artifactStatus || i.artifactStatus === 'active')
      },

      // Get filtered items based on current filters
      getFilteredItems: () => {
        const { filters } = get()
        let result = get().getActiveItems()

        if (filters.status) {
          result = result.filter((i) => i.status === filters.status)
        }
        if (filters.priority) {
          result = result.filter((i) => i.priority === filters.priority)
        }
        if (filters.category) {
          result = result.filter((i) => i.category === filters.category)
        }
        if (filters.sourceType) {
          result = result.filter((i) => i.sourceType === filters.sourceType)
        }
        if (filters.ownerId) {
          result = result.filter((i) => i.ownerId === filters.ownerId)
        }
        if (filters.overdue) {
          const now = new Date()
          result = result.filter(
            (i) => i.dueDate && new Date(i.dueDate) < now && i.status !== 'completed'
          )
        }
        if (filters.deliverableType) {
          result = result.filter((i) => i.deliverableType === filters.deliverableType)
        }
        if (filters.sourceDocumentId) {
          result = result.filter((i) => i.sourceDocumentId === filters.sourceDocumentId)
        }

        return result
      },

      // Get items filtered by the active compliance view
      getViewFilteredItems: () => {
        const { activeView, includeUncertain } = get()
        let items = get().getFilteredItems()

        // Apply view-specific phase filters
        switch (activeView) {
          case 'submission_checklist':
            // Packing list: forms, attachments, evidence, deadlines, Go/No-Go gates
            // Exclude Scored criteria and Narrative deliverables (those are in Compliance Checklist)
            items = items.filter(
              (i) =>
                (i.phase === 'pre_submission' || i.category === 'go_no_go') &&
                (i.category as string) !== 'scored' &&
                !(i.deliverableType === 'Narrative' && i.category !== 'go_no_go')
            )
            // Exclude low-confidence/unknown unless user opts in
            if (!includeUncertain) {
              items = items.filter(
                (i) =>
                  i.category === 'go_no_go' || // Always show Go/No-Go
                  (i.phase !== 'unknown' && i.phaseConfidence !== 'low')
              )
            }
            break
          case 'response_compliance':
            // Critical/scored criteria + narrative requirements (pre-submission)
            items = items.filter(
              (i) =>
                i.phase === 'pre_submission' &&
                (i.category === 'critical' || i.deliverableType === 'Narrative')
            )
            break
          case 'contract':
            // Post-award legal/commercial obligations
            items = items.filter(
              (i) => i.phase === 'post_award'
            )
            break
          case 'delivery':
            // During-works operational obligations
            items = items.filter(
              (i) => i.phase === 'during_works'
            )
            break
          case 'contract_obligations':
            // Legacy: Post-award + during-works items (kept for backwards compat)
            items = items.filter(
              (i) => i.phase === 'post_award' || i.phase === 'during_works'
            )
            break
          case 'all':
            // No phase filtering, show everything
            if (!includeUncertain) {
              items = items.filter(
                (i) => i.phase !== 'unknown' && i.phaseConfidence !== 'low'
              )
            }
            break
        }

        return items
      },

      // Get items by status
      getItemsByStatus: (status: ComplianceItemStatus) => {
        return get().getActiveItems().filter((i) => i.status === status)
      },

      // Get items by priority
      getItemsByPriority: (priority: ComplianceItemPriority) => {
        return get().getActiveItems().filter((i) => i.priority === priority)
      },

      // Get items by category
      getItemsByCategory: (category: ComplianceItemCategory) => {
        return get().getActiveItems().filter((i) => i.category === category)
      },

      // Get items by source type
      getItemsBySourceType: (sourceType: ComplianceSourceType) => {
        return get().getActiveItems().filter((i) => i.sourceType === sourceType)
      },

      // Get overdue items
      getOverdueItems: () => {
        const now = new Date()
        return get()
          .getActiveItems()
          .filter((i) => i.dueDate && new Date(i.dueDate) < now && i.status !== 'completed')
      },

      // Get item by ID
      getItemById: (id: string) => {
        return get().items.find((i) => i.id === id)
      },

      // Get stats (all active items, not filtered by view)
      getStats: () => {
        const activeItems = get().getActiveItems()
        const now = new Date()

        return {
          total: activeItems.length,
          pending: activeItems.filter((i) => i.status === 'pending').length,
          inProgress: activeItems.filter((i) => i.status === 'in_progress').length,
          completed: activeItems.filter((i) => i.status === 'completed').length,
          blocked: activeItems.filter((i) => i.status === 'blocked').length,
          criticalPending: activeItems.filter(
            (i) => i.priority === 'critical' && i.status === 'pending'
          ).length,
          highPending: activeItems.filter((i) => i.priority === 'high' && i.status === 'pending')
            .length,
          overdueCount: activeItems.filter(
            (i) => i.dueDate && new Date(i.dueDate) < now && i.status !== 'completed'
          ).length,
        }
      },

      // Get stats scoped to the active view tab
      getViewStats: () => {
        const viewItems = get().getViewFilteredItems()
        const now = new Date()

        return {
          total: viewItems.length,
          pending: viewItems.filter((i) => i.status === 'pending').length,
          inProgress: viewItems.filter((i) => i.status === 'in_progress').length,
          completed: viewItems.filter((i) => i.status === 'completed').length,
          blocked: viewItems.filter((i) => i.status === 'blocked').length,
          criticalPending: viewItems.filter(
            (i) => i.priority === 'critical' && i.status === 'pending'
          ).length,
          highPending: viewItems.filter((i) => i.priority === 'high' && i.status === 'pending')
            .length,
          overdueCount: viewItems.filter(
            (i) => i.dueDate && new Date(i.dueDate) < now && i.status !== 'completed'
          ).length,
        }
      },
    }),
    { name: 'compliance-items-store' }
  )
)
